import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMsal } from "@azure/msal-react";
import {
  createTrackerRequestId,
  getCurrentSession,
  pauseSession,
  resumeSession,
  startSession,
  stopSession,
  stopSessionOnLogout,
} from "./performanceTrackerApi";
import {
  enqueuePending,
  isRetryableTrackerError,
} from "./trackerPendingQueue";
import {
  isLiveTrackerStatus,
  loadTrackerSnapshot,
  saveTrackerSnapshot,
} from "./trackerSnapshot";
import { useTrackerRuntime } from "./useTrackerRuntime";

const EMPTY_STATE = {
  sessionId: null,
  status: "NOT_STARTED",
  serverTime: null,
  activeSeconds: 0,
  lastActivityTime: null,
  currentIntervalStart: null,
  inactivityThresholdSeconds: null,
  activityHeartbeatSeconds: null,
  enabled: true,
  autoResumeEnabled: true,
  requirePauseComment: true,
  requireStopComment: true,
  autoStopOnLogout: true,
};

const PerformanceTrackerContext = createContext(null);

export function PerformanceTrackerProvider({ children }) {
  const { instance, accounts } = useMsal();
  const [state, setState] = useState(() => loadTrackerSnapshot() || EMPTY_STATE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const busyRef = useRef(false);
  const accountsRef = useRef(accounts);
  const publishRef = useRef(() => {});
  const stateRef = useRef(state);
  accountsRef.current = accounts;
  stateRef.current = state;

  const applyState = useCallback((data) => {
    if (!data || typeof data !== "object" || Array.isArray(data)) return;
    setState((prev) => {
      const next = { ...prev, ...data };
      saveTrackerSnapshot(next);
      return next;
    });
  }, []);

  const refreshCurrent = useCallback(async () => {
    const data = await getCurrentSession(instance, accountsRef.current);
    applyState(data);
    publishRef.current(data);
    return data;
  }, [applyState, instance]);

  const { publishState } = useTrackerRuntime({
    state,
    applyState,
    refreshCurrent,
    instance,
    accountsRef,
  });
  publishRef.current = publishState;

  useEffect(() => {
    const persist = () => saveTrackerSnapshot(stateRef.current);
    window.addEventListener("pagehide", persist);
    return () => window.removeEventListener("pagehide", persist);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getCurrentSession(instance, accountsRef.current);
        if (!cancelled) {
          applyState(data);
          publishRef.current(data);
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 403) {
          setState((prev) => ({ ...prev, enabled: false }));
          return;
        }
        if (isRetryableTrackerError(err)) {
          setError("Offline — showing last known tracker state.");
          return;
        }
        setError(err?.message || "Unable to load tracker");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyState, instance]);

  const runMutation = useCallback(
    async (fn, pendingItem) => {
      if (busyRef.current) return null;
      busyRef.current = true;
      setBusy(true);
      setError("");
      try {
        const data = await fn();
        applyState(data);
        publishRef.current(data);
        setDialog(null);
        return data;
      } catch (err) {
        if (err?.status === 409) {
          try {
            const current = await refreshCurrent();
            setDialog(null);
            setError("");
            return current;
          } catch {
            /* keep the original error */
          }
        }
        if (err?.status === 403) {
          setState((prev) => ({ ...prev, enabled: false }));
        }
        if (pendingItem && isRetryableTrackerError(err)) {
          enqueuePending(pendingItem);
          setError("Offline — last known tracker state is kept and will sync when you are back online.");
          setDialog(null);
          return null;
        }
        setError(err?.message || "Tracker request failed");
        throw err;
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [applyState, refreshCurrent],
  );

  const start = useCallback(
    () => {
      const idempotencyKey = createTrackerRequestId();
      return runMutation(
        () => startSession(instance, accountsRef.current, { idempotencyKey }),
        { kind: "start", idempotencyKey },
      );
    },
    [instance, runMutation],
  );

  const pause = useCallback(
    (comment) => {
      const idempotencyKey = createTrackerRequestId();
      return runMutation(
        () =>
          pauseSession(instance, accountsRef.current, {
            sessionId: state.sessionId,
            comment,
            idempotencyKey,
          }),
        {
          kind: "pause",
          sessionId: state.sessionId,
          comment,
          idempotencyKey,
        },
      );
    },
    [instance, runMutation, state.sessionId],
  );

  const resume = useCallback(
    () => {
      const idempotencyKey = createTrackerRequestId();
      return runMutation(
        () =>
          resumeSession(instance, accountsRef.current, {
            sessionId: state.sessionId,
            resumeType: "MANUAL",
            idempotencyKey,
          }),
        {
          kind: "resume",
          sessionId: state.sessionId,
          resumeType: "MANUAL",
          idempotencyKey,
        },
      );
    },
    [instance, runMutation, state.sessionId],
  );

  const stop = useCallback(
    (comment) => {
      const idempotencyKey = createTrackerRequestId();
      return runMutation(
        () =>
          stopSession(instance, accountsRef.current, {
            sessionId: state.sessionId,
            comment,
            idempotencyKey,
          }),
        {
          kind: "stop",
          sessionId: state.sessionId,
          comment,
          idempotencyKey,
        },
      );
    },
    [instance, runMutation, state.sessionId],
  );

  const stopForLogout = useCallback(async () => {
    const current = stateRef.current;
    if (current.autoStopOnLogout === false) return;
    if (!isLiveTrackerStatus(current.status) || !current.sessionId) return;
    await stopSessionOnLogout(instance, accountsRef.current, {
      sessionId: current.sessionId,
    });
  }, [instance]);

  const value = useMemo(
    () => ({
      ...state,
      busy,
      error,
      dialog,
      setDialog,
      start,
      pause,
      resume,
      stop,
      stopForLogout,
      refreshCurrent,
    }),
    [state, busy, error, dialog, start, pause, resume, stop, stopForLogout, refreshCurrent],
  );

  return (
    <PerformanceTrackerContext.Provider value={value}>
      {children}
    </PerformanceTrackerContext.Provider>
  );
}

export function usePerformanceTracker() {
  const ctx = useContext(PerformanceTrackerContext);
  if (!ctx) {
    throw new Error("usePerformanceTracker must be used within PerformanceTrackerProvider");
  }
  return ctx;
}

export function usePerformanceTrackerOptional() {
  return useContext(PerformanceTrackerContext);
}
